import { Box, CircularProgress, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { createContext, useContext, useState } from "react";

const SnackbarContext = createContext();

export const useSnackbar = () => {
    const ctx = useContext(SnackbarContext);
    if (!ctx) throw new Error("useSnackbar must be used within a GlobalSnackbarProvider");
    return ctx;
};

/* ─── theme tokens (kept local for portability) ─── */
const THEME = {
    surface: "#1F1F1F",
    surfaceBorder: "rgba(255,255,255,0.08)",
    text: "#FFFFFF",
    textMuted: "rgba(255,255,255,0.7)",
    accent: {
        success: "#22C55E",
        error: "#EF4444",
        warning: "#F59E0B",
        info: "#6366F1",
        loading: "var(--primary)",
    },
};

const ICONS = {
    success: CheckCircleRoundedIcon,
    error: ErrorRoundedIcon,
    warning: WarningRoundedIcon,
    info: InfoRoundedIcon,
};

function SlideUp(props) {
    return <Slide {...props} direction="up" />;
}

export const GlobalSnackbarProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("info");
    const [isLoading, setIsLoading] = useState(false);

    const showSnackbar = (msg, sev = "info", loading = false) => {
        setMessage(msg);
        setSeverity(sev);
        setIsLoading(loading);
        setOpen(true);
    };

    const handleClose = (_e, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
        setIsLoading(false);
    };

    const accent = isLoading ? THEME.accent.loading : THEME.accent[severity] || THEME.accent.info;
    const Icon = ICONS[severity] || InfoRoundedIcon;

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}

            <Snackbar
                open={open}
                autoHideDuration={isLoading ? null : 4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                TransitionComponent={SlideUp}
                sx={{ "& .MuiSnackbarContent-root": { p: 0 } }}
            >
                <Box
                    role="alert"
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 320,
                        maxWidth: 420,
                        pl: 2,
                        pr: 1,
                        py: 1.25,
                        bgcolor: THEME.surface,
                        color: THEME.text,
                        border: `1px solid ${THEME.surfaceBorder}`,
                        borderRadius: "14px",
                        overflow: "hidden",
                        backdropFilter: "blur(12px)",
                        boxShadow:
                            "0 10px 30px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.04)",
                        /* accent stripe */
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            bgcolor: accent,
                        },
                    }}
                >
                    {/* icon bubble */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "10px",
                            flexShrink: 0,
                            bgcolor: `${accent}1F`,
                            color: accent,
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={16} thickness={5} sx={{ color: accent }} />
                        ) : (
                            <Icon sx={{ fontSize: 18 }} />
                        )}
                    </Box>

                    {/* message */}
                    <Typography
                        sx={{
                            flex: 1,
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            lineHeight: 1.4,
                            color: THEME.text,
                            letterSpacing: "-0.005em",
                        }}
                    >
                        {message}
                    </Typography>

                    {/* close */}
                    {!isLoading && (
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            sx={{
                                color: THEME.textMuted,
                                "&:hover": { color: THEME.text },
                            }}
                        >
                            <CloseRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};